var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { $loader } from 'ziphy-web-shared/basic/utils';
import TextBlock from '../block/textBlock/TextBlock';
import { $notes } from '../Notes.store';
import MacrosPlus from '../ui/macrosPlus/MacrosPlus';
import styles from './Block.module.scss';
import ResourceBlock from './resourceBlock/ResourceBlock';
import TemplateBlock from './templateBlock/TemplateBlock';
const Block = ({ position, $section, $block, $prevBlock, $nextBlock, parentBlockRef, isPlain, canAdd, }) => {
    const isRoot = !Boolean(parentBlockRef);
    const latestProps = useRef({ position, $section, $block, parentBlockRef });
    useEffect(() => {
        Object.assign(latestProps.current, { position, $block, $section, parentBlockRef });
    }, [position, $section, $block, parentBlockRef]);
    const handleDelete = useCallback($loader.registerHandler(`delete-${$block.ref}`, () => __awaiter(void 0, void 0, void 0, function* () {
        yield $block.delete({ parentBlockRef });
    })), [$block === null || $block === void 0 ? void 0 : $block.ref, parentBlockRef]);
    const handleUpdateTextBlock = useCallback((value, data) => __awaiter(void 0, void 0, void 0, function* () {
        const { position, $block, parentBlockRef, $section } = latestProps.current;
        if ($block.isEmptyTextBlock) {
            yield $notes.insertBlock({
                sectionId: $section.id,
                content: value,
                position: position,
                parentBlockRef,
            }, $block.ref);
        }
        else {
            yield $block.save({ content: value, data });
        }
    }), []);
    const handleRemoveTextBlock = useCallback((type) => __awaiter(void 0, void 0, void 0, function* () {
        let event = null;
        if (type === 'Backspace' && $prevBlock && $prevBlock.type === 'text') {
            event = new CustomEvent(`TextBlockFocus-${$prevBlock.ref}`, {
                detail: { caretPosition: 'end' },
            });
        }
        else if (type === 'Delete' && $nextBlock && $nextBlock.type === 'text') {
            event = new CustomEvent(`TextBlockFocus-${$nextBlock.ref}`, {
                detail: { caretPosition: 'start' },
            });
        }
        if (event) {
            window.dispatchEvent(event);
        }
        if (!$block.isEmptyTextBlock) {
            yield handleDelete();
        }
    }), [handleDelete, $prevBlock === null || $prevBlock === void 0 ? void 0 : $prevBlock.ref, $nextBlock === null || $nextBlock === void 0 ? void 0 : $nextBlock.ref]);
    useEffect(() => {
        const handleFocus = (e) => {
            const { moveTo } = e.detail;
            const focusBlock = moveTo === 'top' ? $prevBlock : $nextBlock;
            if (focusBlock && focusBlock.type === 'text') {
                window.dispatchEvent(new CustomEvent(`TextBlockFocus-${focusBlock.ref}`, {
                    detail: { caretPosition: moveTo === 'top' ? 'end' : 'start' },
                }));
            }
        };
        const eventName = `TextBlockCaretJump-${$block.ref}`;
        window.addEventListener(eventName, handleFocus, false);
        return () => window.removeEventListener(eventName, handleFocus, false);
    }, [$block.ref, $prevBlock === null || $prevBlock === void 0 ? void 0 : $prevBlock.ref, $nextBlock === null || $nextBlock === void 0 ? void 0 : $nextBlock.ref]);
    let content;
    switch ($block.type) {
        case 'template':
            content = _jsx(TemplateBlock, { "$block": $block, isPlain: isPlain });
            break;
        case 'resource':
            content = _jsx(ResourceBlock, { "$block": $block, className: isPlain ? styles.peNone : undefined });
            break;
        case 'text':
            const showTitle = $block.title && (($prevBlock === null || $prevBlock === void 0 ? void 0 : $prevBlock.type) !== 'text' || $block.title !== $prevBlock.title);
            content = (_jsxs(_Fragment, { children: [showTitle && _jsx("div", Object.assign({ className: styles.title }, { children: $block.title })), _jsx(TextBlock, { id: $block.ref, text: $block.text, data: $block.data, errors: $block.errors.empty_placeholder, "$section": $section, macrosList: isRoot ? $section.macros.text : $section.macros.textSecondary, onUpdate: handleUpdateTextBlock, onRemove: handleRemoveTextBlock, isPlain: isPlain })] }));
            break;
        default:
            content = null;
    }
    if (!content) {
        return _jsx(_Fragment, {});
    }
    return (_jsxs("div", Object.assign({ className: classNames(styles.wrapper, $block.type === 'text' && styles.textBlock, !isRoot || ($block.isResource && !canAdd) ? styles.secondLevel : undefined) }, { children: [_jsx("div", Object.assign({ className: styles.content }, { children: content })), !$block.isEmptyTextBlock && !isPlain && (_jsxs("div", Object.assign({ className: styles.buttons }, { children: [_jsx(Button, { mode: ['secondary-icon', 'justIcon', 'xs'], onClick: handleDelete, isLoading: $loader.isRunHandler(`delete-${$block.ref}`), leftElement: 'trash', className: styles.button }), canAdd && (_jsx(MacrosPlus, { "$section": $section, macrosList: $section.macros.plusSecondary, parentBlockRef: $block.ref, buttonClassName: styles.button }))] })))] })));
};
export default observer(Block);
